// 근거 보기: 서버가 준 문장 위치(파이썬 글자 위치)와 근거 조각 번호를 화면용으로 바꾸는 순수 함수들.

export const HOW_TEXT = {
  copy: '넣은 내용을 거의 그대로 썼어요',
  reword: '넣은 내용을 생기부 문장으로 다듬었어요',
  combine: '넣은 내용 여러 개를 한 문장으로 묶었어요',
  summary: '넣은 내용 전체를 정리한 문장이에요',
  direction: '참고 자료의 방향에 맞춰 쓴 문장이에요',
  edited: '고친 문장이에요',
};

export const SOURCE_KIND_ORDER = { typed: 0, keyword: 1, file: 2, direction: 3 };

const hasSurrogates = (text) => /[\uD800-\uDFFF]/.test(text);

/** 서버 위치(코드 포인트 단위)를 JS 문자열 위치(UTF-16)로 바꾸는 함수를 만든다. */
export const codePointToIndex = (content) => {
  const text = String(content || '');
  if (!hasSurrogates(text)) return (position) => position;
  const offsets = [];
  let cursor = 0;
  for (const char of text) {
    offsets.push(cursor);
    cursor += char.length;
  }
  offsets.push(cursor);
  return (position) => offsets[Math.max(0, Math.min(position, offsets.length - 1))];
};

/** 쓴 근거 조각에 넣은 순서(입력 → 키워드 → 파일 → 방향 참고)대로 1부터 번호를 붙인다. */
export const numberUnits = (item, sources = []) => {
  const used = new Set();
  (item?.sentences || []).forEach((sentence) => (sentence.u || []).forEach((unitId) => used.add(unitId)));
  const numbers = new Map();
  orderedSources(sources).forEach((source) => {
    (source.units || []).forEach((unit) => {
      if (used.has(unit.id) && !numbers.has(unit.id)) numbers.set(unit.id, numbers.size + 1);
    });
  });
  used.forEach((unitId) => {
    if (!numbers.has(unitId)) numbers.set(unitId, numbers.size + 1);
  });
  return numbers;
};

/** 문장 하나의 번호들(작은 수부터). */
export const sentenceNumbers = (units, numbers) => (units || [])
  .map((unitId) => numbers.get(unitId))
  .filter(Boolean)
  .sort((a, b) => a - b);

export const indexUnits = (sources) => {
  const byId = new Map();
  (sources || []).forEach((source) => {
    (source.units || []).forEach((unit) => byId.set(unit.id, { unit, source }));
  });
  return byId;
};

/** 본문을 [사이 글, 문장, 사이 글, 문장 …] 조각으로 나눈다. 위치가 본문과 안 맞으면 null. */
export const buildPieces = (content, item) => {
  const text = String(content || '');
  const sentences = item?.sentences || [];
  if (!text || sentences.length === 0) return null;
  const toIndex = codePointToIndex(text);
  const pieces = [];
  let cursor = 0;
  for (let index = 0; index < sentences.length; index += 1) {
    const sentence = sentences[index];
    const start = toIndex(Number(sentence.start));
    const end = toIndex(Number(sentence.end));
    if (!(start >= cursor && end > start && end <= text.length)) return null;
    if (start > cursor) pieces.push({ type: 'gap', text: text.slice(cursor, start) });
    pieces.push({
      type: 'sentence',
      index,
      text: text.slice(start, end),
      start,
      end,
      units: sentence.u || [],
      how: sentence.how || 'summary',
    });
    cursor = end;
  }
  if (cursor < text.length) pieces.push({ type: 'gap', text: text.slice(cursor) });
  return pieces;
};

/** 서버 content_hash 와 같은 값: 앞뒤 공백을 뺀 글의 sha256 앞 16자. 브라우저가 못 하면 null. */
export const hashContent = async (content) => {
  const subtle = typeof window !== 'undefined' ? window.crypto?.subtle : null;
  if (!subtle || typeof TextEncoder === 'undefined') return null;
  try {
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(String(content || '').trim()));
    return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('').slice(0, 16);
  } catch {
    return null;
  }
};

/** 왼쪽 목록 순서: 입력 → 키워드 → 파일 → 방향 참고. */
export const orderedSources = (sources) => [...(sources || [])].sort((a, b) => {
  const rank = (source) => (source.role === 'direction' ? 3 : (SOURCE_KIND_ORDER[source.kind] ?? 2));
  return rank(a) - rank(b);
});

export const describeHow = (sentence, unitsById) => {
  const base = HOW_TEXT[sentence?.how] || HOW_TEXT.summary;
  if (sentence?.how !== 'direction') return base;
  const label = (sentence.units || [])
    .map((unitId) => unitsById.get(unitId)?.source?.label)
    .find(Boolean);
  return label ? `${label} 방향에 맞춰 쓴 문장이에요` : base;
};

/** 근거가 쓰인 문장 위치들(조각 → 문장 목록). */
export const sentencesByUnit = (item) => {
  const map = new Map();
  (item?.sentences || []).forEach((sentence, index) => {
    (sentence.u || []).forEach((unitId) => {
      if (!map.has(unitId)) map.set(unitId, []);
      map.get(unitId).push(index);
    });
  });
  return map;
};

const NUMBERS_KEY = 'whatquiz.saenggibu.evidenceNumbers';

export const readShowNumbers = () => {
  try {
    return window.localStorage.getItem(NUMBERS_KEY) !== 'off';
  } catch {
    return true;
  }
};

export const writeShowNumbers = (value) => {
  try {
    window.localStorage.setItem(NUMBERS_KEY, value ? 'on' : 'off');
  } catch {
    // 저장 못 해도 이번 화면에서는 바뀐다.
  }
  try {
    window.dispatchEvent(new CustomEvent('saenggibuEvidenceNumbers', { detail: { value } }));
  } catch {
    // CustomEvent 가 없는 환경
  }
};
